/**
 * PreviewSiteCMS — CMS integration for the /preview marketing site.
 *
 * Exports:
 *  - usePreviewCMS(): fetches published blocks + super-admin state, exposes
 *    a `blocksByType` lookup that hardcoded sections can merge over their
 *    defaults. Also exposes `saveBlockDraft` / `publish` / `discard` for the
 *    inline edit overlay.
 *  - EditableSection: wraps any section with an "Edit" pill that opens the
 *    BlockEditorDialog for super admins. Non-admins see nothing.
 *  - PublishBar: floating bottom bar shown when there are unpublished
 *    changes, with Publish / Discard / Full editor buttons.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Pencil, Send, X, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { BlockEditorDialog, ensureStarterFields } from './CMSAdmin';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const SLUG = 'preview';

export function usePreviewCMS() {
  const [publishedBlocks, setPublishedBlocks] = useState([]);
  const [draftBlocks, setDraftBlocks] = useState([]);
  const [pageMeta, setPageMeta] = useState(null); // { draft_version, published_version, has_unpublished_changes }
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [token, setToken] = useState(null);
  const [loaded, setLoaded] = useState(false);

  // Detect super-admin session
  useEffect(() => {
    const t = localStorage.getItem('lodgiflow_token');
    if (!t) { setLoaded(true); return; }
    axios.get(`${BACKEND}/api/auth/verify`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => {
        const role = r.data.role;
        const tenantId = r.data.tenant_id || '';
        const superAdmin = role === 'super_admin' || (role === 'admin' && !tenantId);
        setIsSuperAdmin(superAdmin);
        if (superAdmin) setToken(t);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Load published blocks (public endpoint — no auth needed)
  const loadPublic = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/cms/pages/${SLUG}/live`);
      const blocks = (r.data.blocks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setPublishedBlocks(blocks);
    } catch { /* Public endpoint should always work; ignore */ }
  }, []);

  // Load draft (super-admin only) — falls back to published if no draft
  const loadDraft = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${BACKEND}/api/cms/pages/${SLUG}`, { headers: { Authorization: `Bearer ${token}` } });
      const draft = r.data.draft || {};
      const blocks = (draft.blocks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setDraftBlocks(blocks);
      setPageMeta({
        draft_version: draft.version || 0,
        published_version: r.data.published?.version || 0,
        has_unpublished_changes: (draft.version || 0) > (r.data.published?.version || 0),
      });
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { loadPublic(); }, [loadPublic]);
  useEffect(() => { loadDraft(); }, [loadDraft]);

  // Super-admins see the draft (so they preview their unpublished changes inline);
  // regular visitors see the published version.
  const activeBlocks = isSuperAdmin && draftBlocks.length > 0 ? draftBlocks : publishedBlocks;

  const blocksByType = useMemo(() => {
    const map = {};
    activeBlocks.forEach(b => {
      if (b.enabled === false) return;
      // First occurrence wins if there are duplicates
      if (!map[b.type]) map[b.type] = b;
    });
    return map;
  }, [activeBlocks]);

  const saveBlockDraft = useCallback(async (updatedBlock) => {
    if (!token) return;
    const nextBlocks = draftBlocks.map(b => b.id === updatedBlock.id ? updatedBlock : b);
    try {
      await axios.put(
        `${BACKEND}/api/cms/pages/${SLUG}/draft`,
        { blocks: nextBlocks },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Draft saved. Click Publish to make it public.');
      loadDraft();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  }, [token, draftBlocks, loadDraft]);

  const publish = useCallback(async () => {
    if (!token) return;
    try {
      await axios.post(
        `${BACKEND}/api/cms/pages/${SLUG}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Published! The live site now shows your changes.');
      await loadDraft();
      await loadPublic();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    }
  }, [token, loadDraft, loadPublic]);

  const discard = useCallback(async () => {
    if (!token || !pageMeta?.published_version) return;
    if (!window.confirm('Discard all unpublished draft changes and revert to the current live version?')) return;
    try {
      await axios.post(
        `${BACKEND}/api/cms/pages/${SLUG}/discard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Unpublished changes discarded.');
      loadDraft();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Discard failed');
    }
  }, [token, pageMeta, loadDraft]);

  return {
    loaded,
    isSuperAdmin,
    token,
    blocksByType,
    pageMeta,
    saveBlockDraft,
    publish,
    discard,
  };
}

/**
 * EditableSection — wraps a rendered marketing section with an "Edit block"
 * pill for super admins. Clicking opens the BlockEditorDialog; saving PUTs
 * the draft. Non-admins render children unchanged with zero overhead.
 */
export function EditableSection({ block, isSuperAdmin, token, onSave, label, children }) {
  const [editorOpen, setEditorOpen] = useState(false);
  if (!isSuperAdmin || !block) return <>{children}</>;

  const openEditor = (e) => {
    e?.stopPropagation?.();
    setEditorOpen(true);
  };

  const editableBlock = { ...block, content: ensureStarterFields(block.type, block.content) };

  return (
    <div
      className="relative group/cmsedit"
      data-cms-block={block.type}
      data-testid={`cms-editable-${block.type}`}
    >
      {/* Purple dashed outline visible only when hovering (super admin only) */}
      <div className="pointer-events-none absolute inset-0 z-40 opacity-0 group-hover/cmsedit:opacity-100 transition-opacity outline outline-2 outline-dashed outline-fuchsia-500/70 rounded-sm" />
      <button
        onClick={openEditor}
        className="absolute top-3 right-3 z-50 opacity-0 group-hover/cmsedit:opacity-100 transition-opacity bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-[11px] font-semibold px-2.5 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
        title={`Edit ${label || block.type} block`}
        data-testid={`cms-edit-pill-${block.type}`}
      >
        <Pencil className="h-3 w-3" />
        Edit {label || block.type}
      </button>
      {children}
      <BlockEditorDialog
        block={editableBlock}
        open={editorOpen}
        onOpenChange={setEditorOpen}
        token={token}
        onSave={(updated) => { setEditorOpen(false); onSave(updated); }}
      />
    </div>
  );
}

/**
 * PublishBar — floating bottom bar shown to super admins whenever the CMS
 * has unpublished draft changes for the current page.
 */
export function PublishBar({ pageMeta, onPublish, onDiscard, onNavigate }) {
  const [publishing, setPublishing] = useState(false);
  if (!pageMeta) return null;
  const hasChanges = pageMeta.has_unpublished_changes;
  const draftV = pageMeta.draft_version;
  const pubV = pageMeta.published_version;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9998] rounded-full shadow-2xl border backdrop-blur-xl flex items-center gap-1 px-2 py-1.5"
      style={{
        background: hasChanges ? 'rgba(251, 191, 36, 0.98)' : 'rgba(16, 185, 129, 0.95)',
        borderColor: hasChanges ? '#f59e0b' : '#059669',
      }}
      data-testid="cms-publish-bar"
    >
      <div className="pl-3 pr-2 py-0.5">
        {hasChanges ? (
          <div className="text-[11px] font-semibold text-amber-950 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-900 animate-pulse" />
            Draft v{draftV} — {pubV > 0 ? `${draftV - pubV} unpublished change${draftV - pubV === 1 ? '' : 's'}` : 'not yet published'}
          </div>
        ) : (
          <div className="text-[11px] font-semibold text-emerald-950 flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3" />
            Live · v{pubV}
          </div>
        )}
      </div>
      {hasChanges && (
        <>
          <Button
            size="sm"
            className="h-7 text-[11px] gap-1 bg-white/95 text-amber-900 hover:bg-white rounded-full font-semibold"
            onClick={async () => { setPublishing(true); await onPublish(); setPublishing(false); }}
            disabled={publishing}
            data-testid="cms-inline-publish-btn"
          >
            {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            {publishing ? 'Publishing…' : 'Publish'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[11px] gap-1 text-amber-950 hover:bg-white/40 rounded-full"
            onClick={onDiscard}
            data-testid="cms-inline-discard-btn"
          >
            <X className="h-3 w-3" /> Discard
          </Button>
        </>
      )}
      <Button
        size="sm"
        variant="ghost"
        className={`h-7 text-[11px] gap-1 rounded-full ${hasChanges ? 'text-amber-950 hover:bg-white/40' : 'text-emerald-950 hover:bg-white/40'}`}
        onClick={onNavigate}
        data-testid="cms-inline-open-editor-btn"
        title="Open the full CMS admin"
      >
        <ExternalLink className="h-3 w-3" /> Full editor
      </Button>
    </div>
  );
}

/**
 * Small helper: read content from a CMS block with a hardcoded fallback.
 * Usage: cmsText(blocksByType.hero, 'headline_html', 'Default')
 */
export function cmsText(block, key, fallback = '') {
  if (!block) return fallback;
  const v = block.content?.[key];
  return (v === undefined || v === null || v === '') ? fallback : v;
}
